import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { Icon } from '../components/icons';
import { DeleteButton, Empty, ErrorBox, Field, Loading, Modal, PageHead } from '../components/ui';
import { useT } from '../i18n';
import type { Client, ClientContact } from '../types';

export function ClientsPage() {
  const t = useT();
  const [editing, setEditing] = useState<Client | 'new' | null>(null);
  const [search, setSearch] = useState('');
  const clients = useQuery({ queryKey: ['clients'], queryFn: () => api.get<Client[]>('/clients') });
  const list = (clients.data ?? []).filter((c) => {
    if (!search) return true;
    const hay = [c.name, c.address, ...c.contacts.map((x) => `${x.name} ${x.email ?? ''}`)].join(' ').toLowerCase();
    return hay.includes(search.toLowerCase());
  });
  return (
    <>
      <PageHead
        title={t('clients.title')}
        actions={
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            <Icon.plus />
            {t('clients.new')}
          </button>
        }
      />
      <div className="toolbar">
        <input className="input search" type="search" placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} aria-label={t('common.search')} />
      </div>
      <div className="card table-wrap">
        {clients.isLoading ? (
          <Loading />
        ) : list.length === 0 ? (
          <Empty title={t('clients.emptyTitle')} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('clients.name')}</th>
                <th>{t('clients.address')}</th>
                <th>{t('clients.mainContact')}</th>
                <th>{t('clients.phone')}</th>
                <th>{t('clients.email')}</th>
                <th className="right">{t('clients.contacts')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className="clickable" onClick={() => setEditing(c)}>
                  <td><strong>{c.name}</strong>{c.vatNumber && <div className="small muted mono">{c.vatNumber}</div>}</td>
                  <td className="small" style={{ whiteSpace: 'pre-line' }}>{c.address}</td>
                  <td>
                    {c.primaryContact?.name}
                    {c.primaryContact?.role && <div className="small muted">{c.primaryContact.role}</div>}
                  </td>
                  <td className="nowrap">{c.primaryContact?.phone}</td>
                  <td>{c.primaryContact?.email}</td>
                  <td className="right num">{c.contacts.length || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing && <ClientModal client={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

const emptyContact = (isPrimary = false): ClientContact => ({ name: '', role: '', phone: '', email: '', isPrimary });

function ClientModal({ client, onClose }: { client?: Client; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [f, setF] = useState({
    name: client?.name ?? '',
    address: client?.address ?? '',
    vatNumber: client?.vatNumber ?? '',
    currency: client?.currency ?? 'EUR',
    notes: client?.notes ?? '',
  });
  const [contacts, setContacts] = useState<ClientContact[]>(client?.contacts.length ? client.contacts.map((c) => ({ ...c })) : [emptyContact(true)]);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const setContact = (i: number, patch: Partial<ClientContact>) => setContacts(contacts.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const makeMain = (i: number) => setContacts(contacts.map((c, j) => ({ ...c, isPrimary: j === i })));
  const removeContact = (i: number) => {
    const next = contacts.filter((_, j) => j !== i);
    if (next.length && !next.some((c) => c.isPrimary)) next[0] = { ...next[0], isPrimary: true };
    setContacts(next);
  };
  // Rows without a name are ignored, so an empty row doesn't block saving.
  const filled = contacts.filter((c) => c.name.trim());
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: f.name,
        address: f.address || null,
        vatNumber: f.vatNumber || null,
        currency: f.currency,
        notes: f.notes || null,
        contacts: filled.map((c) => ({ name: c.name, role: c.role || null, phone: c.phone || null, email: c.email || null, isPrimary: c.isPrimary })),
      };
      return client ? api.patch(`/clients/${client.id}`, body) : api.post('/clients', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['event'] });
      onClose();
    },
  });
  return (
    <Modal
      wide
      title={client ? t('clients.edit') : t('clients.new')}
      onClose={onClose}
      footer={
        <>
          {client && (
            <DeleteButton
              name={client.name}
              path={`/clients/${client.id}`}
              blockedCode="client_has_events"
              confirmText={t('del.clientText')}
              onDone={() => {
                qc.invalidateQueries({ queryKey: ['clients'] });
                onClose();
              }}
            />
          )}
          <button className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={!f.name || save.isPending} onClick={() => save.mutate()}>{t('common.save')}</button>
        </>
      }
    >
      <ErrorBox error={save.error} />
      <div className="form-grid">
        <Field label={t('clients.name')} full htmlFor="c-name">
          <input id="c-name" className="input" value={f.name} onChange={set('name')} autoFocus />
        </Field>
        <Field label={t('clients.address')} full htmlFor="c-addr">
          <textarea id="c-addr" className="textarea" style={{ minHeight: 60 }} value={f.address} onChange={set('address')} placeholder={t('clients.addressPlaceholder')} />
        </Field>
        <Field label={t('clients.vat')} htmlFor="c-vat">
          <input id="c-vat" className="input" value={f.vatNumber} onChange={set('vatNumber')} />
        </Field>
        <Field label={t('clients.currency')} htmlFor="c-cur">
          <select id="c-cur" className="select" value={f.currency} onChange={set('currency')}>
            {['EUR', 'USD', 'GBP', 'CHF', 'ALL', 'RSD', 'MKD'].map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
      </div>

      <div className="stack" style={{ gap: 10, borderTop: '1px solid var(--line)', paddingTop: 14 }}>
        <div className="spread">
          <h3>{t('clients.contacts')}</h3>
          <button className="btn btn-sm" onClick={() => setContacts([...contacts, emptyContact(contacts.length === 0)])}>
            <Icon.plus />
            {t('clients.addContact')}
          </button>
        </div>
        <p className="muted small" style={{ margin: 0 }}>{t('clients.contactsHint')}</p>
        {contacts.length === 0 && <span className="muted small">{t('clients.noContacts')}</span>}
        {contacts.map((c, i) => (
          <div key={i} className="card" style={{ padding: 12, boxShadow: 'none', background: c.isPrimary ? 'var(--surface-2)' : undefined }}>
            <div className="form-grid">
              <Field label={t('clients.contactName')} htmlFor={`cn-${i}`}>
                <input id={`cn-${i}`} className="input" value={c.name} onChange={(e) => setContact(i, { name: e.target.value })} />
              </Field>
              <Field label={t('clients.contactRole')} htmlFor={`cr-${i}`}>
                <input id={`cr-${i}`} className="input" value={c.role ?? ''} onChange={(e) => setContact(i, { role: e.target.value })} placeholder={t('clients.rolePlaceholder')} />
              </Field>
              <Field label={t('clients.phone')} htmlFor={`cp-${i}`}>
                <input id={`cp-${i}`} className="input" type="tel" value={c.phone ?? ''} onChange={(e) => setContact(i, { phone: e.target.value })} />
              </Field>
              <Field label={t('clients.email')} htmlFor={`ce-${i}`}>
                <input id={`ce-${i}`} className="input" type="email" value={c.email ?? ''} onChange={(e) => setContact(i, { email: e.target.value })} />
              </Field>
            </div>
            <div className="spread" style={{ marginTop: 10 }}>
              <label className="checkbox">
                <input type="radio" name="main-contact" checked={c.isPrimary} onChange={() => makeMain(i)} />
                {t('clients.main')}
              </label>
              <button className="btn btn-sm btn-ghost" onClick={() => removeContact(i)}>{t('common.remove')}</button>
            </div>
          </div>
        ))}
      </div>

      <Field label={t('common.notes')} htmlFor="c-notes">
        <textarea id="c-notes" className="textarea" value={f.notes} onChange={set('notes')} />
      </Field>
    </Modal>
  );
}
